import { useMemo, useState } from "react";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Grid,
  Table,
  TableRow
} from "semantic-ui-react";
import { AccordionTitleView, CommonTable, CustomButton, DropDown } from "../../../components";
import { InputNumber } from "../../../components/InputText/InputNumber";
import { FacilityWaterTable, WATER_SOURCE_UNIT_TYPES } from "../../../config/constants";
import { DEFAULT_WATER_SOURCE } from "../../../config/drawingConstants";
import { formatDecimalValue, validateFacilityNumber } from "../../../utils/utils";
import "./facilityForm.scss";

const FacilityWaterSource = ({
  waterType,
  defaultWaterType,
  setDefaultWaterType,
  errorsFacilityWater,
  handleSubmitFacilityWater,
  addFacilityWater,
  currentFacilityWaterData,
  setDeleteWaterData,
  setVisibleDeleteWaterModal,
  control,
  setValue,
  reset,
  renderTableData = false
}: any) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [getDefaultUnit, setUnit] = useState<string>(WATER_SOURCE_UNIT_TYPES[0].value);

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const selectedWaterType = useMemo(() => {
    return waterType?.find((item: any) => item?.value === String(defaultWaterType));
  }, [waterType, defaultWaterType]);

  const addFacilityWaterData = (data: any) => {
    const price = data?.Price ? Number(data?.Price) : 0;
    let updateData = {
      ...data,
      Price: price,
      UnitType: getDefaultUnit,
      Volumes: isNaN(Number(data?.Volumes)) ? 0 : Number(data?.Volumes),
      WaterSource: Number(defaultWaterType) || DEFAULT_WATER_SOURCE
    };
    if (enableEdit && editIndex !== null) {
      currentFacilityWaterData[editIndex] = updateData;
    } else {
      currentFacilityWaterData.push(updateData);
    }
    reset();
    setDefaultWaterType(DEFAULT_WATER_SOURCE);
    setUnit(WATER_SOURCE_UNIT_TYPES[0].value);
    setEditIndex(null);
    setEnableEdit(false);
  };

  const getWaterData = (data: any) => {
    let groupIngredientData = data.reduce((currentType: any, data: any, index: any) => {
      data.index = index;
      let checkDataIsAvailable = currentType?.find((other: any) => other.key === data.Year);
      if (currentType?.length === 0) {
        let facilityData = {
          key: data.Year,
          value: [data]
        };
        return [...currentType, facilityData];
      } else if (checkDataIsAvailable) {
        let currentIndex = currentType?.findIndex((other: any) => other.key === data.Year);
        let updatedArray = [];

        const updateArray = [...currentType[currentIndex].value, data];
        const updatedObject = {
          ...currentType[currentIndex],
          value: updateArray
        };
        updatedArray = [
          ...currentType.slice(0, currentIndex),
          updatedObject,
          ...currentType.slice(currentIndex + 1)
        ];
        return updatedArray;
      } else {
        let facilityData = {
          key: data.Year,
          value: [data]
        };

        return [...currentType, facilityData];
      }
    }, []);
    return groupIngredientData;
  };

  const setFacilityWaterDetails = (data: any) => {
    setValue("Price", data?.Price);
    setValue("Year", data?.Year);
    setValue("Volumes", data?.Volumes);
    setDefaultWaterType(data?.WaterSource);
    setUnit(data?.UnitType || WATER_SOURCE_UNIT_TYPES[0].value);
    setEditIndex(data?.index);
    setEnableEdit(true);
  };

  const renderTableRow = (dataView: any, rowSpan: any, totalValue: any, onDelete: any) => {
    const waterTypeAsText = waterType?.find(
      (item: any) => item?.value === String(dataView?.WaterSource)
    )?.text;
    return (
      <TableRow key={dataView?.id || dataView?.index}>
        {rowSpan > 0 && (
          <Table.Cell rowSpan={rowSpan}>
            <p>{dataView?.Year ?? ""}</p>
          </Table.Cell>
        )}
        <Table.Cell>
          <p>{waterTypeAsText ?? 0.0}</p>
        </Table.Cell>
        <Table.Cell>
          <p>
            {isNaN(Number(dataView?.Volumes)) ? 0 : dataView?.Volumes ?? 0}
            <span className="unitTypeView">{dataView?.UnitType ?? ""}</span>
          </p>
        </Table.Cell>
        <Table.Cell>
          <p>{formatDecimalValue(dataView?.Price)}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{formatDecimalValue(totalValue)}</p>
        </Table.Cell>
        <Table.Cell>
          {!renderTableData ? (
            <Grid>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdModeEditOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={() => setFacilityWaterDetails(dataView)}
                />
              </Grid.Column>
              <Grid.Column computer={4} tablet={16} mobile={16}>
                <MdDeleteOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={onDelete}
                />
              </Grid.Column>
            </Grid>
          ) : null}
        </Table.Cell>
      </TableRow>
    );
  };

  const loadTableData = () => {
    const groupFacilityByYear = getWaterData(currentFacilityWaterData);
    return groupFacilityByYear.map((groupData: any, groupIndex: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const Volumes = !isNaN(dataView?.Volumes) ? dataView?.Volumes : 0;
        const price = dataView?.Price || 0;
        const totalValue = Volumes * price;

        const handleDelete = () => {
          setDeleteWaterData(dataView);
          setVisibleDeleteWaterModal(true);
        };
        return renderTableRow(dataView, isFirstRow ? rowCount : 0, totalValue, handleDelete);
      });
    });
  };

  return (
    <>
      <Accordion>
        <AccordionTitle active={accordionEnabled} onClick={handleClick}>
          <AccordionTitleView accordionEnabled={accordionEnabled} title={"Water Sources"} />
        </AccordionTitle>
        <AccordionContent active={accordionEnabled}>
          <Grid>
            <Grid.Column
              computer={16}
              tablet={16}
              mobile={16}
              className={!renderTableData ? "userBankDetailsMain" : "recipeTableViewMain"}
            >
              <div>
                {!renderTableData ? (
                  <Grid>
                    <Grid.Column computer={3} tablet={8} mobile={16}>
                      <DropDown
                        labelName={"Source*"}
                        placeholder="Source"
                        currentData={waterType}
                        defaultValue={selectedWaterType?.value || null}
                        customGridColumn={"customGridColomnType"}
                        handleChangeState={(e: any, { value }: any) => {
                          setDefaultWaterType(value);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={3} tablet={8} mobile={16}>
                      <InputNumber
                        control={control}
                        errors={errorsFacilityWater.Year}
                        labelName={"Year*"}
                        placeholder="Year"
                        name="Year"
                        required={true}
                        errorMessage="Year is required"
                        isYearInput={true}
                      />
                    </Grid.Column>
                    <Grid.Column computer={3} tablet={8} mobile={16}>
                      <InputNumber
                        control={control}
                        errors={errorsFacilityWater.Volumes}
                        labelName={"Volume consumed"}
                        placeholder="Volume consumed"
                        name="Volumes"
                        required={false}
                        errorMessage="Volume consumed is required"
                        otherValidation={{
                          validate: {
                            valueCheck: (v: any) => validateFacilityNumber(v)
                          }
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={2} tablet={8} mobile={16}>
                      <DropDown
                        labelName={"Unit Type"}
                        placeholder="Type"
                        currentData={WATER_SOURCE_UNIT_TYPES}
                        defaultValue={getDefaultUnit}
                        customGridColumn={"customGridColomnType"}
                        handleChangeState={(e: any, { value }: any) => {
                          setUnit(value);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={3} tablet={8} mobile={16}>
                      <InputNumber
                        control={control}
                        errors={errorsFacilityWater.Price}
                        labelName={"Price per unit"}
                        placeholder="Price"
                        name="Price"
                        otherValidation={{
                          validate: {
                            valueCheck: (v: any) => validateFacilityNumber(v)
                          }
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column computer={2} tablet={8} mobile={16}>
                      {enableEdit ? (
                        <CustomButton
                          theme="green"
                          customColumnStyle={"addUnitSize"}
                          title="Update"
                          onClick={handleSubmitFacilityWater((data: any) => {
                            addFacilityWaterData(data);
                          })}
                          disabled={
                            errorsFacilityWater && Object.keys(errorsFacilityWater).length > 0
                              ? true
                              : false
                          }
                        />
                      ) : (
                        <CustomButton
                          theme="green"
                          customColumnStyle={"addUnitSize"}
                          icon="plus"
                          onClick={handleSubmitFacilityWater((data: any) => {
                            addFacilityWaterData(data);
                          })}
                          disabled={
                            errorsFacilityWater && Object.keys(errorsFacilityWater).length > 0
                              ? true
                              : false
                          }
                        />
                      )}
                    </Grid.Column>
                  </Grid>
                ) : null}

                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className={!renderTableData ? "productRegisterTable customTableBottom" : ""}
                >
                  {addFacilityWater?.length > 0 && (
                    <CommonTable tableHeaderData={FacilityWaterTable}>
                      {loadTableData()}
                    </CommonTable>
                  )}
                </Grid.Column>
              </div>
            </Grid.Column>
          </Grid>
        </AccordionContent>
      </Accordion>
    </>
  );
};

export default FacilityWaterSource;
